// Tour-Datenschicht: Packliste, Stops, Abschluss, Rückladeliste.

import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/stores/authStore";
import { formatDateIso } from "@/lib/calendar";
import { waehleLaufendeTour, phaseVon, type TourZeile } from "@/lib/laufende-tour";

/**
 * Das Anmelde-Merkmal für die eigenen API-Aufrufe.
 *
 * BEWUSST nicht über supabase.auth.getSession(): Der Aufruf ist asynchron und
 * versucht dabei, den Token zu erneuern — im Funkloch hängt er. Die Mutation
 * kommt dann nie bis zum fetch, es gibt keinen Fehler, und der Knopf tut
 * schweigend nichts. Genau daran gingen erst die Stempel verloren und danach
 * beinahe der Stop-Abschluss: Der Fahrer tippt "kassiert — abschließen", und es
 * passiert nichts, was er sehen könnte.
 *
 * Der Zustand ist beim Anmelden geladen worden und liegt im Speicher — das
 * Auslesen braucht kein Netz.
 */
function token() {
  return useAuth.getState().session?.access_token ?? "";
}

/** Wer ist angemeldet — aus demselben Grund synchron wie token(). */
function meineId() {
  return useAuth.getState().user?.id ?? null;
}

export type StopStatus = "pending" | "arrived" | "delivered" | "absent" | "skipped";

export interface TourStop {
  id: string;
  seq: number;
  status: StopStatus;
  eta_at: string | null;
  completed_at: string | null;
  skip_reason: string | null;
  cash_collected_cents: number | null;
  order: {
    id: string;
    total_cents: number;
    items: { id: string; product_id: string; qty: number; unit_price_cents: number }[];
    customer: {
      id: string;
      display_name: string;
      customer_number: string | null;
      notes: string | null;
      phone: string | null;
      interest_tags: string[];
      preferred_time: string | null;
      // Für den Kundenpflege-Modus: Das Fenster am Stop zeigt und speichert
      // genau diese Felder. Ohne data_confirmed_at wüsste der Fahrer nicht,
      // welcher Kunde noch offen ist.
      email: string | null;
      type: "privat" | "gewerblich" | null;
      data_confirmed_at: string | null;
    };
    address: {
      id: string;
      street: string;
      zip: string;
      city: string;
      geo_lat: number | null;
      geo_lng: number | null;
      delivery_notes: string | null;
    };
  };
}

export interface LoadItem {
  id: string;
  product_id: string;
  kind: "pack" | "return";
  qty_total: number;
  checked_at: string | null;
}

export interface TourFull {
  id: string;
  tour_date: string;
  status: "draft" | "planned" | "released" | "active" | "finished";
  /** Wem gehört die Tour? Wer sie fährt, darf sie bearbeiten — sonst niemand. */
  driver_id: string | null;
  /** Wann wurde mit dem Beladen begonnen? NULL = Packliste noch nicht gestartet. */
  packlist_started_at: string | null;
  packlist_confirmed_at: string | null;
  returnlist_confirmed_at: string | null;
  planned_distance_m: number | null;
  planned_duration_s: number | null;
  stops: TourStop[];
  load_items: LoadItem[];
}

const STOP_SELECT = `
  id, seq, status, eta_at, completed_at, skip_reason, cash_collected_cents,
  order:orders(
    id, total_cents,
    items:order_items(id, product_id, qty, unit_price_cents),
    customer:customers(id, display_name, customer_number, notes, phone, interest_tags, preferred_time, email, type, data_confirmed_at),
    address:customer_addresses(id, street, zip, city, geo_lat, geo_lng, delivery_notes)
  )
`;

export interface LaufendeTour {
  id: string;
  tour_date: string;
  /** Was gerade dran ist — davon hängt ab, was der Streifen zählt. */
  phase: "beladen" | "fahren";
  fertig: number;
  gesamt: number;
}

/**
 * Läuft gerade eine Tour — und wie weit ist sie?
 *
 * Damit der Fahrer aus JEDER Ecke der App mit einem Tipp zurück in seine Tour
 * kommt, muss die ganze App wissen, dass eine läuft. Die dicke Tour-Abfrage
 * (useTour) dafür überall mitlaufen zu lassen, wäre teuer: Sie zieht zu jedem
 * der 158 Stopps Bestellung, Kunde und Adresse. Hier reicht das Nötigste —
 * ein Datum, eine Phase, zwei Zahlen.
 *
 * WELCHE Tour das ist, entscheidet waehleLaufendeTour() — eine reine Funktion
 * ohne Datenbank, damit sie sich prüfen lässt. Hier wird nur geholt und gezählt.
 */
export function useLaufendeTour() {
  return useQuery({
    queryKey: ["tour-laufend"],
    // Die Zahlen im Streifen dürfen ruhig ein paar Sekunden alt sein; beim
    // Zurückwechseln in die App werden sie ohnehin frisch geholt.
    staleTime: 20_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<LaufendeTour | null> => {
      // formatDateIso, nicht toISOString(): Letzteres rechnet nach UTC um und
      // macht aus "23.07., 00:30 deutscher Zeit" den 22.07.
      const heute = formatDateIso(new Date());

      const { data, error } = await supabase
        .from("delivery_tours")
        .select("id, tour_date, status, packlist_started_at, packlist_confirmed_at")
        .lte("tour_date", heute)
        .neq("status", "finished")
        .order("tour_date", { ascending: false })
        .limit(10);
      if (error) throw error;

      const t = waehleLaufendeTour((data ?? []) as TourZeile[], heute);
      if (!t) return null;

      if (phaseVon(t) === "beladen") {
        const { data: posten, error: e2 } = await supabase
          .from("tour_load_items")
          .select("checked_at")
          .eq("tour_id", t.id);
        if (e2) throw e2;
        const alle = posten ?? [];
        return {
          id: t.id,
          tour_date: t.tour_date,
          phase: "beladen",
          fertig: alle.filter((p) => p.checked_at).length,
          gesamt: alle.length,
        };
      }

      const { data: stops, error: e3 } = await supabase
        .from("delivery_stops")
        .select("status")
        .eq("tour_id", t.id);
      if (e3) throw e3;
      const alle = (stops ?? []) as { status: StopStatus }[];
      return {
        id: t.id,
        tour_date: t.tour_date,
        phase: "fahren",
        // "arrived" heisst: Er steht davor, ist aber noch nicht durch.
        fertig: alle.filter((s) => s.status !== "pending" && s.status !== "arrived").length,
        gesamt: alle.length,
      };
    },
  });
}

/**
 * Welche Touren stehen an?
 *
 * Der Wagen wird am ABEND VORHER gepackt — die Tour von morgen muss also heute
 * schon erreichbar sein. Und sobald es mehrere Liefertage gibt (Mo–Fr), reicht
 * "der nächste Dienstag" nicht mehr.
 *
 * Gezeigt wird ein Fenster von gestern bis in einer Woche: Gestern, weil eine
 * gefahrene Tour noch die Rückladeliste offen haben kann.
 */
export function useAnstehendeTouren() {
  return useQuery({
    queryKey: ["touren-anstehend"],
    staleTime: 60_000,
    queryFn: async () => {
      const heute = new Date();
      const von = new Date(heute);
      von.setDate(von.getDate() - 1);
      const bis = new Date(heute);
      bis.setDate(bis.getDate() + 7);
      const vonIso = von.toISOString().slice(0, 10);
      const bisIso = bis.toISOString().slice(0, 10);

      // Das Zeitfenster (gestern bis in einer Woche) ist die normale Planung.
      //
      // ODER noch nicht abgeschlossen — EGAL wie alt. Ohne diesen zweiten Teil
      // verschwindet eine Tour, an der noch gearbeitet wird, einfach aus der App,
      // sobald ihr Datum zwei Tage her ist: Man öffnet die Touren und liest "Für
      // diesen Tag gibt es keine Tour", während die eigene laufende Tour mit 221
      // offenen Stops in der Datenbank steht. Genau das ist passiert.
      const { data, error } = await supabase
        .from("delivery_tours")
        .select("id, tour_date, status, packlist_confirmed_at, returnlist_confirmed_at")
        .or(`and(tour_date.gte.${vonIso},tour_date.lte.${bisIso}),status.neq.finished`)
        .order("tour_date");
      if (error) throw error;

      return (data ?? []) as {
        id: string;
        tour_date: string;
        status: string;
        packlist_confirmed_at: string | null;
        returnlist_confirmed_at: string | null;
      }[];
    },
  });
}

export function useTour(date: string) {
  return useQuery({
    queryKey: ["tour", date],
    queryFn: async () => {
      // Bewusst KEIN maybeSingle(): liegen für ein Datum versehentlich mehrere
      // Touren in der DB, würde das einen Fehler werfen und die ganze Seite
      // lahmlegen. Stattdessen nehmen wir die zuletzt gebaute — die mit Stops.
      //
      // Und bewusst OHNE Filter auf 'finished': genau in diesem Zustand entsteht
      // die Rückladeliste. Filterte man ihn weg, wäre die Liste zwar in der DB,
      // aber für den Fahrer unerreichbar — das Einlagern fiele stillschweigend aus.
      const { data, error } = await supabase
        .from("delivery_tours")
        .select(
          `id, tour_date, status, driver_id, packlist_started_at, packlist_confirmed_at,
           returnlist_confirmed_at, planned_distance_m, planned_duration_s,
           load_items:tour_load_items(id, product_id, kind, qty_total, checked_at),
           stops:delivery_stops(${STOP_SELECT})`
        )
        .eq("tour_date", date)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const tours = (data ?? []) as unknown as TourFull[];
      if (!tours.length) return null;

      const t = tours.find((x) => (x.stops ?? []).length > 0) ?? tours[0];
      t.stops = (t.stops ?? []).sort((a, b) => a.seq - b.seq);
      return t;
    },
    refetchOnWindowFocus: true,
  });
}

/**
 * Packlisten-Position abhaken (optimistisch — das Beladen darf nicht ruckeln).
 *
 * Wichtig: `onSettled` invalidiert NUR, wenn keine weitere Mutation mehr läuft.
 * Sonst überschreibt die Antwort eines früheren Klicks den Zustand eines
 * späteren (schnelles An-/Abhaken landete in der falschen Endposition).
 */
export function useToggleLoadItem(date: string) {
  const qc = useQueryClient();
  const inFlight = useRef(0);

  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Genau daran gingen die Stempel verloren, und
    // hier hängt Ausgeliefertes, Kassiertes und Abgerechnetes dran.
    // Mit "always" läuft sie und scheitert SICHTBAR, wenn kein Netz da ist.
    networkMode: "always",
    mutationFn: async ({ id, checked }: { id: string; checked: boolean }) => {
      const { error } = await supabase
        .from("tour_load_items")
        .update({ checked_at: checked ? new Date().toISOString() : null })
        .eq("id", id);
      if (error) throw error;
    },
    onMutate: async ({ id, checked }) => {
      inFlight.current += 1;
      await qc.cancelQueries({ queryKey: ["tour", date] });
      const prev = qc.getQueryData<TourFull>(["tour", date]);
      qc.setQueryData<TourFull>(["tour", date], (t) =>
        t
          ? {
              ...t,
              load_items: t.load_items.map((li) =>
                li.id === id ? { ...li, checked_at: checked ? new Date().toISOString() : null } : li
              ),
            }
          : t
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["tour", date], ctx.prev);
    },
    onSettled: () => {
      inFlight.current -= 1;
      if (inFlight.current === 0) qc.invalidateQueries({ queryKey: ["tour", date] });
    },
  });
}

/**
 * "Ab jetzt wird geladen."
 *
 * Setzt den Startzeitpunkt des Beladens. Erst danach zeigt die App die
 * Packliste — der Fahrer (oder wer abends packt) sagt bewusst Bescheid, statt
 * beim Öffnen des Tages mitten in einer Liste zu landen.
 */
export function usePacklisteStarten(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Genau daran gingen die Stempel verloren, und
    // hier hängt Ausgeliefertes, Kassiertes und Abgerechnetes dran.
    // Mit "always" läuft sie und scheitert SICHTBAR, wenn kein Netz da ist.
    networkMode: "always",
    mutationFn: async (tourId: string) => {
            const { error } = await supabase
        .from("delivery_tours")
        .update({
          packlist_started_at: new Date().toISOString(),
          packlist_started_by: meineId(),
        })
        .eq("id", tourId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tour", date] }),
  });
}

export function useConfirmPacklist(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Genau daran gingen die Stempel verloren, und
    // hier hängt Ausgeliefertes, Kassiertes und Abgerechnetes dran.
    // Mit "always" läuft sie und scheitert SICHTBAR, wenn kein Netz da ist.
    networkMode: "always",
    mutationFn: async (tourId: string) => {
            const { error } = await supabase
        .from("delivery_tours")
        .update({
          packlist_confirmed_at: new Date().toISOString(),
          packlist_confirmed_by: meineId(),
        })
        .eq("id", tourId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tour", date] }),
  });
}

export function useStartTour(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Genau daran gingen die Stempel verloren, und
    // hier hängt Ausgeliefertes, Kassiertes und Abgerechnetes dran.
    // Mit "always" läuft sie und scheitert SICHTBAR, wenn kein Netz da ist.
    networkMode: "always",
    mutationFn: async (tourId: string) => {
            const { error } = await supabase
        .from("delivery_tours")
        .update({
          status: "active",
          started_at: new Date().toISOString(),
          driver_id: meineId(),
        })
        .eq("id", tourId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tour", date] }),
  });
}

/**
 * Stop abschließen. Läuft über /api/tour?action=complete, weil dabei serverseitig
 * Lieferschein UND Rechnung entstehen — das darf nicht im Client passieren.
 */
export function useCompleteStop(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Genau daran gingen die Stempel verloren, und
    // hier hängt Ausgeliefertes, Kassiertes und Abgerechnetes dran.
    // Mit "always" läuft sie und scheitert SICHTBAR, wenn kein Netz da ist.
    networkMode: "always",
    mutationFn: async (input: {
      stopId: string;
      outcome: "delivered" | "absent";
      paymentMethod?: "cash" | "invoice";
      cashCents?: number;
      signatureDataUrl?: string;
      reason?: string;
    }) => {
            const res = await fetch("/api/tour?action=complete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? "Fehler beim Abschließen");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tour", date] }),
  });
}

/** Tour beenden → erzeugt serverseitig die Rückladeliste aus den nicht gelieferten Stops. */
export function useFinishTour(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Genau daran gingen die Stempel verloren, und
    // hier hängt Ausgeliefertes, Kassiertes und Abgerechnetes dran.
    // Mit "always" läuft sie und scheitert SICHTBAR, wenn kein Netz da ist.
    networkMode: "always",
    mutationFn: async (tourId: string) => {
            const res = await fetch("/api/tour?action=finish", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ tourId }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? "Fehler");
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tour", date] }),
  });
}

/**
 * Die Tour eines Tages jetzt bauen — Reihenfolge optimieren, Stops, Packliste.
 *
 * Normalerweise macht das der nächtliche Lauf von selbst. Diesen Knopf gibt es,
 * weil man sonst hilflos wäre, wenn der Lauf mal nicht kommt: Dann stünden 200
 * Bestellungen in der Datenbank und die App sagte "Für diesen Tag gibt es keine
 * Tour", ohne dass man etwas tun kann. Genau das war tagelang der Fall.
 */
export function useTourBauen(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Genau daran gingen die Stempel verloren, und
    // hier hängt Ausgeliefertes, Kassiertes und Abgerechnetes dran.
    // Mit "always" läuft sie und scheitert SICHTBAR, wenn kein Netz da ist.
    networkMode: "always",
    mutationFn: async () => {
            const res = await fetch("/api/tour?action=route", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ date }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error ?? "Tour konnte nicht gebaut werden");
      return j as { stops_count: number; distance_km: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tour", date] });
      qc.invalidateQueries({ queryKey: ["touren-anstehend"] });
    },
  });
}

/**
 * Eine leere Tour für einen Tag anlegen, an dem es keine gibt.
 *
 * "Tour bauen" hilft nur, wenn für den Tag Bestellungen vorliegen — an einem Tag
 * ohne Abos gibt es keine, und die App bot bis dahin nichts an. Die Kunden kommen
 * danach einzeln über die Warteliste dazu.
 */
export function useTourAnlegen(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — der Knopf täte dann wortlos nichts.
    networkMode: "always",
    mutationFn: async () => {
      const res = await fetch("/api/tour?action=tour-create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ date }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error ?? "Tour konnte nicht angelegt werden");
      return j as { tourId: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tour", date] });
      qc.invalidateQueries({ queryKey: ["touren-anstehend"] });
    },
  });
}

/**
 * Notfall-Übernahme durch einen Admin.
 *
 * Eine laufende Tour gehört dem Fahrer allein. Fällt er aus, schreibt der Admin
 * die Tour mit diesem Aufruf auf sich um — danach IST er der Fahrer und alle
 * Bearbeiten-Wege öffnen sich für ihn. Nur ein Admin kommt durch (der Server
 * prüft es; siehe tourUebernehmen).
 */
export function useTourUebernehmen(date: string) {
  const qc = useQueryClient();
  return useMutation({
    networkMode: "always",
    mutationFn: async (tourId: string) => {
      const res = await fetch("/api/tour?action=tour-uebernehmen", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ tourId }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error ?? "Die Tour konnte nicht übernommen werden");
      return j;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tour", date] });
      qc.invalidateQueries({ queryKey: ["touren-anstehend"] });
    },
  });
}

/**
 * Kunde zur Tour hinzufügen — Bestellung entsteht serverseitig aus dem Abo-Korb.
 * Siehe api/_lib/handlers/tour-kunden.ts. Der Stop landet am Ende der Reihenfolge.
 */
export function useKundeZurTour(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Genau daran gingen die Stempel verloren, und
    // hier hängt Ausgeliefertes, Kassiertes und Abgerechnetes dran.
    // Mit "always" läuft sie und scheitert SICHTBAR, wenn kein Netz da ist.
    networkMode: "always",
    mutationFn: async (v: { tourId: string; customerId: string }) => {
            const res = await fetch("/api/tour?action=stop-add", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify(v),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? "Konnte nicht hinzugefügt werden");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tour", date] });
      qc.invalidateQueries({ queryKey: ["touren-anstehend"] });
    },
  });
}

/**
 * Den Korb an der Haustür ändern, bevor übergeben wird.
 *
 * Der Kunde entscheidet vor Ort, was er dazunimmt und was nicht. Läuft über den
 * Server, weil an diesen Positionen Lieferschein und Rechnung hängen: Die Preise
 * kommen aus dem Produktkatalog und die Summe rechnet der Server — schickte das
 * Gerät sie mit, bestimmte der Absender, was die Ware kostet.
 */
export function useStopKorb(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — der Fahrer tippt die Menge, und es passiert
    // wortlos nichts. Mit "always" scheitert sie SICHTBAR.
    networkMode: "always",
    mutationFn: async (v: { stopId: string; items: { product_id: string; qty: number }[] }) => {
      const res = await fetch("/api/tour?action=stop-korb", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify(v),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error ?? "Korb konnte nicht geändert werden");
      return j as { total_cents: number; positionen: number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tour", date] }),
  });
}

/** Kunde von der Tour entfernen — nur solange der Stop noch aussteht (pending). */
export function useKundeVonTour(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Genau daran gingen die Stempel verloren, und
    // hier hängt Ausgeliefertes, Kassiertes und Abgerechnetes dran.
    // Mit "always" läuft sie und scheitert SICHTBAR, wenn kein Netz da ist.
    networkMode: "always",
    mutationFn: async (stopId: string) => {
            const res = await fetch("/api/tour?action=stop-remove", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token()}`,
        },
        body: JSON.stringify({ stopId }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? "Konnte nicht entfernt werden");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tour", date] });
      qc.invalidateQueries({ queryKey: ["touren-anstehend"] });
    },
  });
}

/**
 * Neue Fahrreihenfolge sichern.
 *
 * Übergeben wird die VOLLSTÄNDIGE Liste der offenen Stopps in Wunschreihenfolge
 * — kein "verschiebe X hinter Y". Eine Aktion müsste der Server gegen einen
 * Stand auflösen, den er nicht kennt; genau daran ist in diesem Projekt schon
 * einmal ein Umschalter lautlos gescheitert (Mutation las ihren Zielzustand aus
 * dem Cache, den das optimistische Update vorher schon gedreht hatte).
 *
 * Läuft über eine Datenbank-Funktion, weil `unique (tour_id, seq)` zwei Schritte
 * erzwingt und die in EINER Transaktion liegen müssen (siehe Migration 045).
 */
export function useStopsUmsortieren(date: string) {
  const qc = useQueryClient();
  return useMutation({
    // Ohne "always" PAUSIERT TanStack Query die Mutation, sobald der Browser
    // sich für offline hält — die Funktion läuft nie, es gibt keinen Fehler, der
    // Knopf tut einfach nichts. Hier hängt die Fahrreihenfolge dran.
    networkMode: "always",
    mutationFn: async ({ tourId, stopIds }: { tourId: string; stopIds: string[] }) => {
      const { error } = await supabase.rpc("stops_umsortieren", {
        p_tour: tourId,
        p_stops: stopIds,
      });
      if (error) throw new Error(error.message);
    },
    onMutate: async ({ stopIds }) => {
      await qc.cancelQueries({ queryKey: ["tour", date] });
      const prev = qc.getQueryData<TourFull>(["tour", date]);
      // Die Liste muss beim Loslassen SOFORT stehen bleiben. Wartet sie auf die
      // Antwort, springt die gezogene Zeile für einen Moment zurück an ihren
      // alten Platz — der Fahrer denkt, es hat nicht geklappt, und zieht nochmal.
      qc.setQueryData<TourFull>(["tour", date], (t) => {
        if (!t) return t;
        // Die offenen Stopps tauschen nur die Plätze, die sie ohnehin belegen —
        // genauso rechnet die Datenbank-Funktion. Erledigte bleiben stehen.
        const plaetze = t.stops.filter((s) => s.status === "pending").map((s) => s.seq).sort((a, b) => a - b);
        const neu = new Map(stopIds.map((id, i) => [id, plaetze[i]]));
        return {
          ...t,
          stops: t.stops
            .map((s) => (neu.has(s.id) ? { ...s, seq: neu.get(s.id)! } : s))
            .sort((a, b) => a.seq - b.seq),
        };
      });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["tour", date], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["tour", date] }),
  });
}

/** Google-Maps-Deeplink auf EIN Ziel — läuft nativ in CarPlay / Android Auto. */
export function mapsUrl(stop: TourStop) {
  const a = stop.order.address;
  const dest =
    a.geo_lat && a.geo_lng
      ? `${a.geo_lat},${a.geo_lng}`
      : encodeURIComponent(`${a.street}, ${a.zip} ${a.city}`);
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
}

/** Nächster offener Stop in Fahrreihenfolge. */
export function nextStop(t: TourFull | null | undefined) {
  return t?.stops.find((s) => s.status === "pending" || s.status === "arrived") ?? null;
}
